import { Badge } from '@client/components/ui/badge';
import { useAccountDetailForm } from '@client/hooks/useAccountDetailForm';
import { cn } from '@client/lib';
import { selectClient } from '@client/model/selectors';
import { ASSET_OPTIONS } from '@client/util/constants';
import { formatCurrency } from '@shared';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AccountDetailStepper } from './AccountDetailStepper';
import { AccountDetailSummary } from './AccountDetailSummary';
import { AssetDeleteButton } from './AssetDeleteButton';

interface AccountDetailsAccordionProps {
  className?: string;
  selectedAccountTypes?: string[];
  showValueBadge?: boolean; // When true, shows value badge instead of checkmark
}

type SaveState = 'idle' | 'saving' | 'saved';

interface LoadingOverlayProps {
  state: SaveState;
}

function LoadingOverlay({ state }: LoadingOverlayProps) {
  if (state === 'idle') return null;

  return (
    <div
      className={cn(
        'absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-white/90 backdrop-blur-sm transition-opacity duration-500',
        state === 'saved' ? 'opacity-0' : 'opacity-100',
      )}
    >
      {state === 'saving' && (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="text-purple h-8 w-8 animate-spin" />
          <p className="text-sm font-medium text-gray-700">Saving...</p>
        </div>
      )}
      {state === 'saved' && (
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
            <CheckIcon className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-sm font-medium text-green-600">Saved</p>
        </div>
      )}
    </div>
  );
}

interface AccountDetailItemProps {
  selfFundingName: string;
  selfFundingSource: SelfFundingSource | undefined;
  fundingSourceId: string | null;
  accountType: string;
  isExpanded: boolean;
  isComplete: boolean;
  hasNextIncomplete: boolean;
  showValueBadge?: boolean;
  onToggle: () => void;
  onNavigateNext: () => void;
  onSaveComplete: () => void;
  onDeleted: () => void;
}

function AccountDetailItem({
  selfFundingName,
  selfFundingSource,
  fundingSourceId,
  accountType,
  isExpanded,
  isComplete,
  hasNextIncomplete,
  showValueBadge = false,
  onToggle,
  onNavigateNext,
  onSaveComplete,
  onDeleted,
}: AccountDetailItemProps) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const prevIsSavingRef = useRef(false);
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { formState, formErrors, formHasBeenSubmitted, isSaving, handleChangeField, handleSubmit } =
    useAccountDetailForm(fundingSourceId, accountType);

  useEffect(() => {
    if (!isComplete) {
      setIsEditMode(true);
    } else {
      setIsEditMode(false);
    }
  }, [isComplete, isExpanded]);

  const handleNext = useCallback(() => {
    if (hasNextIncomplete) {
      onNavigateNext();
    } else {
      onToggle();
    }
  }, [hasNextIncomplete, onNavigateNext, onToggle]);

  useEffect(() => {
    const prevIsSaving = prevIsSavingRef.current;

    if (!prevIsSaving && isSaving) {
      setSaveState('saving');
    } else if (prevIsSaving && !isSaving && saveState === 'saving') {
      setSaveState('saved');

      navigationTimeoutRef.current = setTimeout(() => {
        handleNext();
        setSaveState('idle');
      }, 1200);
    }

    prevIsSavingRef.current = isSaving;
  }, [isSaving, saveState, handleNext]);

  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  const handleEdit = useCallback(() => {
    setIsEditMode(true);
  }, []);

  const handleSubmitWrapper = useCallback(() => {
    const success = handleSubmit();
    if (success) {
      setIsEditMode(false);
      onSaveComplete();
    }
  }, [handleSubmit, onSaveComplete]);

  const showSummary = isComplete && !isEditMode;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-gray-900">{selfFundingName}</span>
        </div>

        <div className="flex items-center gap-3">
          {!fundingSourceId && (
            <AssetDeleteButton
              accountType={accountType}
              fundingSourceId={fundingSourceId}
              selfFundingName={selfFundingName}
              onDeleted={onDeleted}
            />
          )}
          {showValueBadge
            ? selfFundingSource?.existingAssetsValue != null && (
                <Badge
                  variant="secondary"
                  className="font-medium"
                >
                  {formatCurrency(selfFundingSource.existingAssetsValue)}
                </Badge>
              )
            : isComplete && (
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-100">
                  <CheckIcon className="h-3 w-3 text-green-700" />
                </div>
              )}
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          )}
        </div>
      </button>

      {/* Content */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden border-t border-gray-100"
          >
            <div className="px-5 pb-5 pt-4">
              <div className="mx-auto flex max-w-md flex-col items-stretch">
                <AnimatePresence mode="wait">
                  {showSummary && selfFundingSource ? (
                    <motion.div
                      key="summary"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="relative"
                    >
                      <AccountDetailSummary
                        selfFundingSource={selfFundingSource}
                        onEdit={handleEdit}
                        deleteButton={
                          fundingSourceId ? (
                            <AssetDeleteButton
                              accountType={accountType}
                              fundingSourceId={fundingSourceId}
                              selfFundingName={selfFundingName}
                              onDeleted={onDeleted}
                              variant="button"
                            />
                          ) : undefined
                        }
                      />
                      <LoadingOverlay state={saveState} />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="stepper"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="relative"
                    >
                      <AccountDetailStepper
                        formState={formState}
                        formErrors={formErrors}
                        onChangeField={handleChangeField}
                        onSubmit={handleSubmitWrapper}
                      />
                      <LoadingOverlay state={saveState} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function AccountDetailsAccordion({
  className,
  selectedAccountTypes = [],
  showValueBadge = false,
}: AccountDetailsAccordionProps) {
  const client = useSelector(selectClient);

  // Create unique set: all selected account types + all existing funding sources (deduped)
  const accountShells = useMemo(() => {
    const selfFunding = client.multipleFundingSources?.selfFunding || {};
    const shells: Array<{
      accountType: string;
      fundingSourceId: string | null;
      selfFundingSource: SelfFundingSource | undefined;
    }> = [];
    const processedAccountTypes = new Set<string>();

    // First, process all selected account types
    selectedAccountTypes.forEach(accountType => {
      // Try to find existing funding source that matches this account type
      const matchingEntry = Object.entries(selfFunding).find(
        ([_, source]) =>
          source.accountType === accountType || source.selfFundingName === accountType,
      );

      if (matchingEntry) {
        // Existing funding source found
        const [fundingSourceId, source] = matchingEntry;
        shells.push({
          accountType,
          fundingSourceId,
          selfFundingSource: source as SelfFundingSource,
        });
      } else {
        // No funding source yet - create shell
        shells.push({
          accountType,
          fundingSourceId: null,
          selfFundingSource: undefined,
        });
      }
      processedAccountTypes.add(accountType);
    });

    // Then, add any existing funding sources that weren't already included
    Object.entries(selfFunding).forEach(([fundingSourceId, source]) => {
      const accountType = source.accountType || source.selfFundingName;

      // Skip if already processed or accountType is undefined
      if (!accountType || processedAccountTypes.has(accountType)) {
        return;
      }

      shells.push({
        accountType,
        fundingSourceId,
        selfFundingSource: source as SelfFundingSource,
      });
      processedAccountTypes.add(accountType);
    });

    return shells;
  }, [selectedAccountTypes, client.multipleFundingSources]);

  // Helper function to check if a self-funding source is complete
  const isSelfFundingSourceComplete = useCallback((source: SelfFundingSource): boolean => {
    return (
      source.existingAssetsValue !== null &&
      source.annualRateOfReturn !== null &&
      source.gainsTaxRate !== null
    );
  }, []);

  // Find first incomplete account type (by index in selectedAccountTypes)
  const autoExpandedAccountType = useMemo(() => {
    const incompleteShell = accountShells.find(
      shell => !shell.selfFundingSource || !isSelfFundingSourceComplete(shell.selfFundingSource),
    );
    return incompleteShell?.accountType || null;
  }, [accountShells, isSelfFundingSourceComplete]);

  const [manuallyExpandedAccountType, setManuallyExpandedAccountType] = useState<string | null>(
    null,
  );

  const expandedAccountType = manuallyExpandedAccountType ?? autoExpandedAccountType;

  const handleToggle = (accountType: string) => {
    setManuallyExpandedAccountType(expandedAccountType === accountType ? null : accountType);
  };

  const getNextIncompleteAccountType = useCallback(
    (currentAccountType: string) => {
      const currentIndex = accountShells.findIndex(
        shell => shell.accountType === currentAccountType,
      );
      for (let i = currentIndex + 1; i < accountShells.length; i++) {
        const shell = accountShells[i];
        if (!shell.selfFundingSource || !isSelfFundingSourceComplete(shell.selfFundingSource)) {
          return shell.accountType;
        }
      }
      return null;
    },
    [accountShells, isSelfFundingSourceComplete],
  );

  const handleNavigateNext = useCallback(
    (currentAccountType: string) => {
      const nextAccountType = getNextIncompleteAccountType(currentAccountType);
      if (nextAccountType) {
        setManuallyExpandedAccountType(nextAccountType);
      }
    },
    [getNextIncompleteAccountType],
  );

  const handleSaveComplete = useCallback((accountType: string) => {
    setManuallyExpandedAccountType(accountType);
  }, []);

  const handleDeleted = useCallback(
    (accountType: string) => {
      // If the deleted item was expanded, collapse it
      if (expandedAccountType === accountType) {
        setManuallyExpandedAccountType(null);
      }
    },
    [expandedAccountType],
  );

  return (
    <div className={cn('space-y-4', className)}>
      {accountShells.map(shell => {
        const accountTypeLabel = ASSET_OPTIONS.find(
          asset => asset.type === shell.accountType,
        )?.label;

        const selfFundingName =
          shell.selfFundingSource?.selfFundingName || accountTypeLabel || shell.accountType;
        const isExpanded = expandedAccountType === shell.accountType;
        const isComplete = shell.selfFundingSource
          ? isSelfFundingSourceComplete(shell.selfFundingSource)
          : false;
        const nextIncompleteAccountType = getNextIncompleteAccountType(shell.accountType);
        const hasNextIncomplete = nextIncompleteAccountType !== null;

        return (
          <AccountDetailItem
            key={shell.accountType}
            selfFundingName={selfFundingName}
            selfFundingSource={shell.selfFundingSource}
            fundingSourceId={shell.fundingSourceId}
            accountType={shell.accountType}
            isExpanded={isExpanded}
            isComplete={isComplete}
            hasNextIncomplete={hasNextIncomplete}
            showValueBadge={showValueBadge}
            onToggle={() => handleToggle(shell.accountType)}
            onNavigateNext={() => handleNavigateNext(shell.accountType)}
            onSaveComplete={() => handleSaveComplete(shell.accountType)}
            onDeleted={() => handleDeleted(shell.accountType)}
          />
        );
      })}
    </div>
  );
}
